import { useEffect, useRef } from "react";
import { useSetRecoilState, useRecoilValue } from "recoil";
import {
  zoomSettingsState,
  zoomSettingsLoadingState,
  zoomSettingsErrorState,
  zoomSettingsInitializedState,
} from "../recoil/zoomSettings";
import {
  getZoomSettings,
  subscribeToZoomSettings,
} from "../services/firestore";
import { validateZoomSettings } from "../models/zoomSettings";

// Zoom Settings 초기화 훅
export const useInitializeZoomSettings = () => {
  const setZoomSettings = useSetRecoilState(zoomSettingsState);
  const setLoading = useSetRecoilState(zoomSettingsLoadingState);
  const setError = useSetRecoilState(zoomSettingsErrorState);
  const setInitialized = useSetRecoilState(zoomSettingsInitializedState);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;

    const initializeSettings = async () => {
      hasInitialized.current = true;
      setLoading(true);
      setError(null);

      try {
        // Firestore에서 데이터 가져오기
        const settings = await getZoomSettings();

        if (settings && validateZoomSettings(settings)) {
          setZoomSettings(settings);
          setInitialized(true);
        } else {
          console.warn("❌ Invalid zoom settings data or document not found");
          setInitialized(true); // 기본값으로 초기화 완료 처리
        }
      } catch (error) {
        console.error("Failed to initialize zoom settings:", error);
        setError(error.message);
        setInitialized(true); // 에러가 있어도 초기화 완료 처리
      } finally {
        setLoading(false);
      }
    };

    initializeSettings();
  }, []); // 빈 의존성 배열로 변경
};

// Zoom Settings 실시간 구독 훅
export const useSubscribeToZoomSettings = () => {
  const setZoomSettings = useSetRecoilState(zoomSettingsState);
  const setError = useSetRecoilState(zoomSettingsErrorState);

  useEffect(() => {
    const unsubscribe = subscribeToZoomSettings((settings) => {
      if (settings && validateZoomSettings(settings)) {
        setZoomSettings(settings);
      } else {
        console.warn(
          "Invalid zoom settings data received from real-time listener"
        );
      }
    });

    return () => unsubscribe();
  }, [setZoomSettings, setError]);
};

// Zoom Settings 사용 훅
export const useZoomSettings = () => {
  const settings = useRecoilValue(zoomSettingsState);
  const loading = useRecoilValue(zoomSettingsLoadingState);
  const error = useRecoilValue(zoomSettingsErrorState);
  const initialized = useRecoilValue(zoomSettingsInitializedState);

  return {
    settings,
    loading,
    error,
    initialized,
    isReady: initialized && !loading && !error,
  };
};
